/* The checkerboard that warps under the cursor.
 *
 * A single WebGL canvas behind the page. There is no image: the checker is
 * worked out per pixel from the coordinate, and the coordinate is pushed
 * around before the test, so the grid appears to bend near the pointer and
 * sit still everywhere else.
 *
 * It sits at z-index -2 so chaos mode's floaters, which live at -1, still
 * pass in front of it. Nothing here takes the pointer.
 *
 * Off on touch and on anything under 1025px, where there is no cursor to
 * follow and the fill rate is not worth spending. The frame loop stops
 * itself once the warp has settled, so a still mouse costs nothing.
 */

const CELL = 17.4;      // checker cell, in css pixels (26 less 33%)
const STRENGTH = 0.55;  // how hard the grid is pushed
const REACH = 320;      // how far from the cursor it dies away
const CONTRAST = 6;     // difference between the two squares, out of 255

const VERT = `
attribute vec2 a;
void main() { gl_Position = vec4(a, 0.0, 1.0); }
`;

/* The displacement here reads one axis from the other on purpose: the
   horizontal shift comes from y, the vertical from x. That is what gives it
   the sliding, banded character rather than a churn. */
const FRAG = `
precision highp float;
uniform vec2  uMouse;
uniform float uOff;
uniform float uTime;
uniform float uStr;
uniform float uPx;
uniform float uRadius;
uniform vec3  uC1;
uniform vec3  uC2;

void main() {
  vec2 p = gl_FragCoord.xy;
  vec2 d = p - uMouse;
  float r = length(d);
  vec2 dir = r > 0.0001 ? d / r : vec2(1.0, 0.0);

  float fall = exp(-(r * r) / (uRadius * uRadius));
  float s = uStr * fall;

  float wx = sin(p.y / uPx * 0.6 + uTime * 1.3) * s * uPx * 1.5
           + cos(p.y / uPx * 1.3 + uTime * 0.7) * s * uPx * 0.4;
  float wy = cos(p.x / uPx * 1.1 + uTime * 0.9) * s * uPx * 0.7
           + sin(p.x / uPx * 0.4 + uTime * 1.7) * s * uPx * 0.3;
  float push = exp(-(r * r) / (uRadius * uRadius) * 5.6) * s * uPx * 2.2;

  // the checker is read in document space so it scrolls with the page,
  // while the cursor maths stays in viewport space
  vec2 w = p + vec2(0.0, uOff) + vec2(wx, wy) - dir * push;

  float ck = mod(floor(w.x / uPx) + floor(w.y / uPx), 2.0);
  vec3 col = mix(uC1, uC2, ck);
  gl_FragColor = vec4(col, 1.0);
}
`;

let live = null;

function supported() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(pointer: coarse)').matches) return false;
  if (window.matchMedia('(max-width: 1024px)').matches) return false;
  try {
    const t = document.createElement('canvas');
    return !!(t.getContext('webgl') || t.getContext('experimental-webgl'));
  } catch (e) {
    return false;
  }
}

function build() {
  const canvas = document.createElement('canvas');
  canvas.style.cssText =
    'position:fixed;top:0;left:0;z-index:-2;pointer-events:none;display:block;';
  document.body.insertBefore(canvas, document.body.firstChild);

  const gl = canvas.getContext('webgl', { alpha: false, antialias: false })
          || canvas.getContext('experimental-webgl', { alpha: false, antialias: false });
  if (!gl) { canvas.remove(); return null; }

  const compile = (type, src) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) return null;
    return s;
  };
  const vs = compile(gl.VERTEX_SHADER, VERT);
  const fs = compile(gl.FRAGMENT_SHADER, FRAG);
  if (!vs || !fs) { canvas.remove(); return null; }

  const prog = gl.createProgram();
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { canvas.remove(); return null; }
  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const aLoc = gl.getAttribLocation(prog, 'a');
  gl.enableVertexAttribArray(aLoc);
  gl.vertexAttribPointer(aLoc, 2, gl.FLOAT, false, 0, 0);

  const u = {};
  ['uMouse', 'uOff', 'uTime', 'uStr', 'uPx', 'uRadius', 'uC1', 'uC2'].forEach((n) => {
    u[n] = gl.getUniformLocation(prog, n);
  });

  return { canvas, gl, u };
}

export function startWarp() {
  if (live || !supported()) return;
  const ctx = build();
  if (!ctx) return;

  const { canvas, gl, u } = ctx;
  const dpr = window.devicePixelRatio || 1;

  const st = { hover: 0, target: 0, mx: 0, my: 0, time: 0, raf: null, last: 0 };

  // the two greys come off the site's own background, so dark mode follows
  function colours() {
    const dark = document.body.classList.contains('dark-mode');
    const base = dark ? 0x1a / 255 : 1;
    const other = dark ? base + CONTRAST / 255 : base - CONTRAST / 255;
    return [[base, base, base], [other, other, other]];
  }

  function resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    gl.viewport(0, 0, canvas.width, canvas.height);
    draw();
  }

  function draw() {
    const [c1, c2] = colours();
    gl.uniform2f(u.uMouse, st.mx, st.my);
    gl.uniform1f(u.uOff, window.scrollY * dpr);
    gl.uniform1f(u.uTime, st.time);
    gl.uniform1f(u.uStr, st.hover * STRENGTH);
    gl.uniform1f(u.uPx, CELL * dpr);
    gl.uniform1f(u.uRadius, REACH * dpr);
    gl.uniform3fv(u.uC1, c1);
    gl.uniform3fv(u.uC2, c2);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  function tick(ts) {
    const dt = Math.min((ts - st.last) / 1000, 0.05);
    st.last = ts;
    st.time += dt;
    st.hover += (st.target - st.hover) * Math.min(dt * 5, 1);
    draw();
    if (st.target > 0 || st.hover > 0.005) {
      st.raf = requestAnimationFrame(tick);
    } else {
      st.hover = 0;
      draw();
      st.raf = null;
    }
  }

  function kick() {
    if (st.raf) return;
    st.last = performance.now();
    st.raf = requestAnimationFrame(tick);
  }

  const onMove = (e) => {
    st.mx = e.clientX * dpr;
    st.my = (window.innerHeight - e.clientY) * dpr;
    st.target = 1;
    kick();
  };
  const onLeave = () => { st.target = 0; kick(); };
  const onScroll = () => { if (!st.raf) draw(); };

  window.addEventListener('resize', resize);
  document.addEventListener('mousemove', onMove);
  document.documentElement.addEventListener('mouseleave', onLeave);
  window.addEventListener('scroll', onScroll, { passive: true });

  // dark mode is a class on body, so watch for it and repaint
  const obs = new MutationObserver(() => { if (!st.raf) draw(); });
  obs.observe(document.body, { attributes: true, attributeFilter: ['class'] });

  resize();

  live = {
    stop() {
      if (st.raf) cancelAnimationFrame(st.raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('mousemove', onMove);
      document.documentElement.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('scroll', onScroll);
      obs.disconnect();
      canvas.remove();
      live = null;
    },
  };
}

export function stopWarp() {
  if (live) live.stop();
}
